type Experience = {
  company: string;
  role: string;
  period: string;
  location: string;
  stack: string[];
  highlights: string[];
};

const skillGroups = [
  {
    title: "Backend",
    items: ["PHP", "Laravel", "Symfony", "Zend Framework", "Yii", "CodeIgniter", "REST", "SOAP"],
  },
  {
    title: "Frontend",
    items: ["React", "TypeScript", "JavaScript", "HTML5", "CSS3", "Sass", "jQuery", "Twig"],
  },
  {
    title: "Data",
    items: ["MySQL", "MariaDB", "PostgreSQL", "MongoDB", "MS SQL", "Oracle", "Redis", "IBM DB2"],
  },
  {
    title: "Delivery",
    items: ["Docker", "Docker Compose", "Git", "Linux", "nginx", "Apache", "Jira", "CI pipelines"],
  },
];

const experiences: Experience[] = [
  {
    company: "Freelance Software Development",
    role: "Senior PHP Software Developer",
    period: "Sep 2024 - Apr 2026",
    location: "Remote worldwide",
    stack: ["PHP", "web applications", "consulting"],
    highlights: [
      "Delivered software tasks for companies worldwide through freelance portals.",
      "Focused on backend development, integrations, maintenance, and production fixes.",
    ],
  },
  {
    company: "Hit Promotional Products",
    role: "Senior Software Developer",
    period: "Mar 2023 - Aug 2024",
    location: "Largo, Florida",
    stack: ["PHP", "Yii", "MySQL", "MongoDB", "IBM DB2", "jQuery", "Docker"],
    highlights: [
      "Enhanced customer portal and finance reporting workflows.",
      "Implemented Auth0 as a security application layer.",
      "Built jobs exchanging EDI, XML, and CSV data.",
    ],
  },
  {
    company: "E.ON / Wipro",
    role: "Team Lead PHP Software Developer",
    period: "Sep 2020 - Dec 2022",
    location: "Remote",
    stack: ["PHP 7.4", "Symfony 6", "Powercloud", "MySQL", "REST APIs", "queues"],
    highlights: [
      "Led Powercloud integration work for one of Germany's largest energy companies.",
      "Created event-driven APIs, background jobs, invoice logic, payment allocations, and dunning workflows.",
      "Guided developers with task assignment, reviews, and daily agile delivery.",
    ],
  },
  {
    company: "Apleona",
    role: "Full-stack Senior Developer",
    period: "May 2020 - Sep 2021",
    location: "Remote: Bern, London, Dusseldorf",
    stack: ["PHP 7.4", "Laravel 8", "PostgreSQL", "JavaScript", "Docker", "GitLab CI"],
    highlights: [
      "Created and developed the CoronaTraceApp SafeAtWork.",
      "Built development and production Docker environments and CI deployment flows.",
      "Implemented backend, frontend, data model, and security features for certification needs.",
    ],
  },
  {
    company: "meetago",
    role: "PHP Senior Software Developer",
    period: "Jul 2017 - Feb 2020",
    location: "Rodgau, Germany",
    stack: ["PHP 7.4", "Laravel", "MySQL", "Redis", "React", "Sass", "Jenkins"],
    highlights: [
      "Developed day-hotel web tools for DB, HRS, and related customers.",
      "Designed backend architecture and an API system with roughly 200 endpoints.",
      "Documented interfaces with Swagger 3 and expanded frontend structures.",
    ],
  },
  {
    company: "Unister - auto.de",
    role: "PHP / MySQL Developer",
    period: "Jan 2008 - Oct 2011",
    location: "Leipzig, Germany",
    stack: ["PHP", "Zend Framework", "MySQL", "REST", "SOAP", "CSV", "XML", "jQuery"],
    highlights: [
      "Built high-performance portal features for up to one million visitors per day.",
      "Designed database systems, procedures, triggers, and large external dealer interfaces.",
      "Developed SEO page systems, editorial workflows, and community features.",
    ],
  },
];

const timeline = [
  "2001 - 2006: Web design and technical support at BMW",
  "2007: PHP Junior Software Developer at CarCopy",
  "2008 - 2011: PHP Software Developer at Unister",
  "2011 - 2013: PHP Senior Software Developer at bitComposer AG",
  "2013 - 2022: Freelance software development",
  "2023 - 2024: Senior Software Developer at Hit Promotional Products",
  "2024 - 2026: Freelance software development",
];

function App() {
  return (
    <main>
      <section className="hero">
        <div className="hero__content">
          <p className="eyebrow">Personal homepage CV</p>
          <h1>Oliver Maslo</h1>
          <p className="role">Senior Software Developer</p>
          <p className="intro">
            PHP, database, and web platform specialist with more than two decades of delivery across
            high-traffic portals, enterprise integrations, energy systems, ecommerce, games, and
            SaaS products.
          </p>
          <div className="hero__actions" aria-label="Contact links">
            <a href="mailto:Oliver.Maslo@gmx.com">Email</a>
            <a href="tel:+18638992773">Call</a>
          </div>
        </div>
        <aside className="profile">
          <img src="/oliver-maslo.png" alt="Oliver Maslo" />
          <div>
            <strong>Lakeland, Florida</strong>
            <span>Available for senior software, backend, and integration work</span>
          </div>
        </aside>
      </section>

      <section className="summary" aria-labelledby="summary-title">
        <div>
          <p className="section-label">Profile</p>
          <h2 id="summary-title">Senior engineer for practical, production-focused web systems.</h2>
        </div>
        <p>
          Oliver combines hands-on development with technical leadership: architecture, backend
          implementation, API design, database migrations, Docker environments, code reviews, and
          team guidance. His project history includes E.ON, BMW, Hit Promotional Products, Apleona,
          meetago, S-KON, bitComposer, Unister, and multiple freelance engagements.
        </p>
      </section>

      <section className="stats" aria-label="Career highlights">
        <div>
          <strong>20+</strong>
          <span>years professional web development</span>
        </div>
        <div>
          <strong>200+</strong>
          <span>API endpoints built in one hotel tooling platform</span>
        </div>
        <div>
          <strong>C1</strong>
          <span>English, with German as native language</span>
        </div>
      </section>

      <section className="section" aria-labelledby="skills-title">
        <div className="section__header">
          <p className="section-label">Capabilities</p>
          <h2 id="skills-title">Core skills</h2>
        </div>
        <div className="skill-grid">
          {skillGroups.map((group) => (
            <article className="skill-card" key={group.title}>
              <h3>{group.title}</h3>
              <ul>
                {group.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="section" aria-labelledby="experience-title">
        <div className="section__header">
          <p className="section-label">Experience</p>
          <h2 id="experience-title">Selected work history</h2>
        </div>
        <div className="experience-list">
          {experiences.map((job) => (
            <article className="experience" key={`${job.company}-${job.period}`}>
              <div className="experience__meta">
                <p>{job.period}</p>
                <span>{job.location}</span>
              </div>
              <div className="experience__body">
                <h3>{job.company}</h3>
                <p className="experience__role">{job.role}</p>
                <div className="tags">
                  {job.stack.map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
                <ul>
                  {job.highlights.map((highlight) => (
                    <li key={highlight}>{highlight}</li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="section two-column" aria-labelledby="credentials-title">
        <div>
          <p className="section-label">Credentials</p>
          <h2 id="credentials-title">Certifications and education</h2>
          <ul className="clean-list">
            <li>Zend Certified Engineer PHP</li>
            <li>Microsoft Certified Professional</li>
            <li>Certified Novell Administrator / Network Engineer</li>
            <li>Microsoft SQL Server developer training</li>
            <li>State-certified vocational degree in electrical systems technology</li>
          </ul>
        </div>
        <div>
          <p className="section-label">Timeline</p>
          <h2>Career path</h2>
          <ul className="timeline">
            {timeline.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      <footer>
        <div>
          <strong>Oliver Maslo</strong>
          <span>Senior Software Developer</span>
        </div>
        <a href="mailto:Oliver.Maslo@gmx.com">Oliver.Maslo@gmx.com</a>
      </footer>
    </main>
  );
}

export default App;
